import {
  createReducer,
  ActionType,
  createAction,
  createAsyncAction,
} from "typesafe-actions";
import { SignInModel, SignInResult, UserSecurityDto } from "Api/Api";
import { AccessRightCodes } from "Components/Models/AccessRights";

export type AppUser = {
  login: string;
  firstName: string;
  lastName: string;
  accessRightCodes: AccessRightCodes[];
};

/**
 * Type of global state type, that handles "user"
 */
export type AuthState = {
  user: AppUser | null;
  errorCode: string | null;
  isLoading: boolean;
};

/**
 * Action that can update state
 */
export const resetUser = createAction("@auth/RESET")<void>();

/**
 * Async action must use sagas
 */
export const signInAsync = createAsyncAction(
  "@auth/SIGN_IN_REQUEST",
  "@auth/SIGN_IN_SUCCESS",
  "@auth/SIGN_IN_FAILURE",
)<SignInModel, SignInResult, Error>();

export const refreshAccessRightsAsync = createAsyncAction(
  "@auth/REFRESH_ACCESS_RIGHTS_REQUEST",
  "@auth/REFRESH_ACCESS_RIGHTS_SUCCESS",
  "@auth/REFRESH_ACCESS_RIGHTS_FAILURE",
)<void, UserSecurityDto | null, Error>();

/**
 * Collection of actions, that can change state
 */
export type AuthAction =
  | ActionType<typeof signInAsync>
  | ActionType<typeof resetUser>
  | ActionType<typeof refreshAccessRightsAsync>;

/**
 * State is updated here.
 *
 * The most important rule of using Redux architecture is:
 *
 * NEVER MUTATE STATE !!!!!!!!!!!!!
 *
 * ALWAYS CREATE NEW OBJECT !!!
 */
export const authReducer = createReducer<AuthState, AuthAction>({
  user: null,
  errorCode: null,
  isLoading: false,
})
  .handleAction(signInAsync.success, (state, action) => {
    if (action.payload.isSuccessful) {
      const user: AppUser = {
        login: action.payload.login,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        accessRightCodes: (action.payload.accessRightCodes ||
          []) as AccessRightCodes[],
      };

      return { ...state, user, errorCode: null, isLoading: false };
    }

    return { ...state, errorCode: action.payload.errorMessage };
  })
  .handleAction(signInAsync.request, state => {
    return { ...state, isLoading: true };
  })
  .handleAction(signInAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })
  .handleAction(resetUser, state => {
    return { ...state, user: null };
  })
  .handleAction(refreshAccessRightsAsync.success, (state, action) => {
    let user = state.user;
    if (!user || !action.payload) {
      return {
        ...state,
      };
    }

    return {
      ...state,
      user: {
        ...user,
        accessRightCodes: action.payload.accessRightCodes as AccessRightCodes[],
      },
    };
  });
